import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Train, 
  Heart, 
  Shield, 
  ArrowRight,
  Layers,
  Lock,
  FileCheck,
  Users,
  Scale,
  Play
} from 'lucide-react';
import AuthorisedDeliveryChain from './AuthorisedDeliveryChain';

const DemoCard = ({ icon: Icon, title, description, domain, href, color }) => (
  <Link 
    to={href}
    className={`block p-6 bg-slate-800/50 border border-slate-700 rounded-xl hover:border-${color}-500/50 transition-all group`}
  >
    <div className="flex items-start gap-4">
      <div className={`p-3 bg-${color}-500/10 rounded-xl group-hover:bg-${color}-500/20 transition-colors`}>
        <Icon className={`w-8 h-8 text-${color}-400`} />
      </div>
      <div className="flex-1">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xl font-semibold text-white">{title}</h3>
          <ArrowRight className="w-5 h-5 text-slate-500 group-hover:text-white transition-colors" />
        </div>
        <p className="text-slate-400 text-sm mb-3">{description}</p>
        <div className="text-xs text-slate-500">{domain}</div>
      </div>
    </div>
  </Link>
);

const Principle = ({ icon: Icon, title, description }) => (
  <div className="flex items-start gap-3">
    <div className="p-2 bg-slate-800 rounded-lg">
      <Icon className="w-4 h-4 text-cyan-400" />
    </div>
    <div>
      <div className="text-sm font-medium text-white">{title}</div>
      <div className="text-xs text-slate-500">{description}</div>
    </div>
  </div>
);

const DemoLanding = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800">
      {/* Navigation */}
      <nav className="border-b border-slate-800">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/technical" className="text-slate-500 hover:text-slate-300 text-sm">
            Technical Dashboard →
          </Link>
          <Link to="/governance" className="text-cyan-400 hover:text-cyan-300 text-sm">
            How KATLAS Works →
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-cyan-500/10 border border-cyan-500/20 rounded-full mb-6">
            <Layers className="w-4 h-4 text-cyan-400" />
            <span className="text-cyan-400 text-sm">KATLAS Governance Demonstrators</span>
          </div>
          
          <h1 className="text-4xl font-bold text-white mb-4">
            Three Workflows, One Governance Substrate
          </h1>
          
          <p className="text-lg text-slate-400 max-w-3xl mx-auto mb-8">
            See how the same run-time operational governance pattern supports different 
            multi-actor workflows. Each demo shows the user experience at the edge and 
            the governance logic beneath it.
          </p>

          {/* Guided Walkthrough CTA */}
          <Link
            to="/walkthrough"
            data-testid="start-walkthrough-btn"
            className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-cyan-600 to-emerald-600 hover:from-cyan-500 hover:to-emerald-500 text-white font-semibold rounded-xl shadow-lg shadow-cyan-500/20 transition-all hover:scale-105"
          >
            <Play className="w-5 h-5" />
            <span>Start Guided Walkthrough</span>
            <span className="text-cyan-200 text-sm">(2-4 min)</span>
          </Link>
        </div>
      </section>

      {/* Authorised Delivery Chain */}
      <section className="pb-8">
        <div className="max-w-5xl mx-auto px-6">
          <AuthorisedDeliveryChain />
        </div>
      </section>

      {/* Demo Cards */}
      <section className="pb-12">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-6">
            <DemoCard
              icon={Train}
              title="Assisted Travel Advisor"
              description="A wheelchair user requests assistance for their rail journey. See how station staff, operators, and coordinators work together with governed handoffs."
              domain="Travel · Accessibility · Multi-operator"
              href="/demos/travel"
              color="cyan"
            />
            <DemoCard
              icon={Heart}
              title="Assisted Patient Advisor"
              description="A patient navigates a cardiology referral pathway. See how GPs, specialists, and care coordinators share only what's necessary."
              domain="Healthcare · Referrals · Pathways"
              href="/demos/patient"
              color="rose"
            />
            <DemoCard
              icon={Scale}
              title="Brief & Advocate Allocation"
              description="Advocate validation, scoped brief sharing, and attributable allocation updates. See how CPS manages governance for prosecution support."
              domain="Legal · CPS · Advocate validation"
              href="/demos/cps"
              color="indigo"
            />
          </div>
        </div>
      </section>

      {/* Shared Governance */}
      <section className="py-12 bg-slate-800/30">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex items-center gap-2 mb-6">
            <Shield className="w-5 h-5 text-cyan-400" />
            <h2 className="text-xl font-semibold text-white">Shared Governance Pattern</h2>
          </div>
          
          <p className="text-slate-400 mb-8">
            All demos are powered by the same KATLAS governance substrate. 
            The actors and decision gates differ, but the principles remain constant.
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            <Principle
              icon={Lock}
              title="Custody"
              description="Data remains with the responsible role unless governed disclosure is necessary"
            />
            <Principle
              icon={Shield}
              title="Authority"
              description="Requests and actions are checked at run time against role, purpose, and scope"
            />
            <Principle
              icon={FileCheck}
              title="Receipts"
              description="Each governed handoff produces attributable evidence of what was shared and why"
            />
          </div>

          <div className="mt-8 p-4 bg-slate-900/50 rounded-lg border border-slate-700">
            <div className="flex items-center gap-3">
              <Users className="w-5 h-5 text-slate-400" />
              <div>
                <div className="text-sm text-white">For reviewers and procurement stakeholders</div>
                <div className="text-xs text-slate-500">
                  Each demo includes a "Show Governance" toggle to reveal the authority checks, 
                  disclosure decisions, and handoff receipts beneath the user experience.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-slate-800">
        <div className="max-w-5xl mx-auto px-6 text-center text-sm text-slate-500">
          KATLAS Governance Substrate · Run-time operational governance for multi-actor workflows
        </div>
      </footer>
    </div>
  );
};

export default DemoLanding;
